import { motion } from "framer-motion";
import type { Player } from "../../types/game";
import { calcNetWorth } from "../../engine/scoring";
import { useLanguage } from "../../i18n/LanguageContext";
import WLBGauge from "./WLBGauge";
import AnimatedNumber from "../ui/AnimatedNumber";

const ICON_EMOJI: Record<string, string> = {
  degree: "🎓", trade_cert: "🔧", skill_badge: "💻", network: "🤝", language: "🌍", leadership: "⭐",
};

interface Props { player: Player; isActive: boolean; rank?: number; }

export default function PlayerPanel({ player, isActive, rank }: Props) {
  const { t } = useLanguage();
  const netWorth = calcNetWorth(player);
  const uniqueIcons = [...new Set(player.prepIcons)];

  return (
    <motion.div
      className={`rounded-2xl border transition-all duration-300 ${isActive ? "bg-gradient-to-br from-slate-700 to-slate-800 border-earnings/50 shadow-lg shadow-earnings/10 p-4" : "bg-slate-800/60 border-white/5 p-3"}`}
      animate={isActive ? { scale: 1.02 } : { scale: 1 }}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-2xl">{player.avatarKey}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            {isActive && <span className="w-2 h-2 rounded-full bg-earnings animate-pulse" />}
            <span className="font-display font-bold text-white truncate text-sm">{player.name}</span>
          </div>
          {rank !== undefined && <div className="text-xs text-white/40">#{rank + 1}</div>}
        </div>
        {player.isBurntOut && (
          <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full font-bold">{t.player.burnoutBadge}</span>
        )}
      </div>

      <div className="flex items-center justify-between mb-2">
        <AnimatedNumber value={player.cash} prefix="🪙 " className="font-display font-black text-gold text-lg" />
        <div className="text-right">
          <div className="text-xs text-white/40">{t.player.netWorth}</div>
          <AnimatedNumber value={netWorth} prefix="💎 " className="font-display font-bold text-white text-sm" />
        </div>
      </div>

      <WLBGauge value={player.wlb} label={t.player.wlb} />

      {isActive && uniqueIcons.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {uniqueIcons.map((icon) => {
            const count = player.prepIcons.filter((i) => i === icon).length;
            const label = t.icons[icon as keyof typeof t.icons] ?? icon;
            return (
              <span key={icon} className="relative group/skill cursor-default">
                <span className="text-xs bg-preparation/20 text-purple-300 px-2 py-0.5 rounded-full inline-block">
                  {ICON_EMOJI[icon]}{count > 1 ? ` ×${count}` : ""}
                </span>
                {/* tooltip */}
                <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 whitespace-nowrap bg-slate-700 border border-white/10 text-white text-xs px-2 py-1 rounded-lg shadow-lg opacity-0 group-hover/skill:opacity-100 transition-opacity duration-150 z-20">
                  {label}{count > 1 ? ` ×${count}` : ""}
                </span>
              </span>
            );
          })}
        </div>
      )}

      {isActive && player.investments.length > 0 && (
        <div className="mt-2 text-xs text-white/50">
          {t.player.investments(player.investments.length)}
          {" · "}
          {t.player.passiveIncome(player.investments.reduce((s, i) => s + i.passiveIncomePerRound, 0))}
        </div>
      )}
    </motion.div>
  );
}
